const PERMISSION_SCOPES = Object.freeze({ READ: 'READ', WRITE: 'WRITE' });

/**
 * @typedef {Object} RBACPermission
 * @property {string} path - Tree path of the feature for which permission is
 * granted
 * @property {string} scope - Level of permission granted for the feature
 */

/**
 * The deserialized role-based access control JWT cookie payload
 * @typedef {Object} PermissionsCookiePayload
 * @property {boolean} fromCache Whether the cookie data was
 * obtained from the User API cache
 * @property {string} ne Suite environment the cookie belongs to
 * @property {string} pk User's external UUID
 * @property {string} [gk] User's ghosting UUID
 * @property {RBACPermission[]} permissions User's granted permissions
 */

/**
 * Check permissions payload for a feature path
 * @param {PermissionsCookiePayload} permCookiePayload
 * @param {!string} path Dot notated feature path
 * @param {string} [scope = 'READ'] The access level (read|write)
 * @returns {boolean}
 */
export default function hasPermission(permCookiePayload, path, scope = PERMISSION_SCOPES.READ) {
  if (!path) {
    throw new Error('no feature provided to check permissions for');
  }

  const perms = permCookiePayload?.permissions;

  if (!Array.isArray(perms) || perms.length === 0) {
    return false;
  }

  // Create list of ancestors from root to this feature, inclusive
  const segments = path.split('.');
  const allowed = [];
  let permit = false;
  let ancestor = '';

  segments.forEach((v, i) => {
    ancestor = segments.slice(0, i + 1).join('.');
    allowed.push(ancestor);
  });

  // Check each permission in permCookie for this feature, explicit or implied
  perms.every(perm => {
    // check permission for this exact feature path or any of its ancestors
    if (allowed.includes(perm.path)) {
      permit = perm.scope === PERMISSION_SCOPES.WRITE || perm.scope === scope;

      return false;
    }

    // check for a descendent of this feature path
    if (perm.path.startsWith(path)) {
      // only 'READ' can be implied upward
      permit = scope === PERMISSION_SCOPES.READ;

      return false;
    }

    return true;
  });

  return permit;
}
